import { useEffect, useState, useCallback } from "react";
import { WifiOff, Wifi, AlertTriangle, RefreshCw } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface OfflineIndicatorProps {
  className?: string;
  position?: 'bottom-right' | 'bottom-center' | 'top';
}

/**
 * Main Offline Indicator Component
 * Shows status when offline and briefly when back online
 */
export function OfflineIndicator({ 
  className,
  position = 'bottom-right'
}: OfflineIndicatorProps) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(!navigator.onLine);
  const [showAlert, setShowAlert] = useState(!navigator.onLine);

  const handleOnline = useCallback(() => {
    setIsOnline(true);
    // Show "back online" message briefly
    setShowAlert(true);
    setTimeout(() => {
      setShowAlert(false);
      setWasOffline(false);
    }, 3000);
  }, []);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setWasOffline(true);
    setShowAlert(true);
  }, []);

  useEffect(() => {
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [handleOnline, handleOffline]);

  if (!showAlert) {
    return null;
  }

  const positionClasses = {
    'bottom-right': 'fixed bottom-4 right-4',
    'bottom-center': 'fixed bottom-4 left-1/2 -translate-x-1/2',
    'top': 'fixed top-0 left-0 right-0'
  };

  return (
    <div className={cn(
      positionClasses[position],
      'z-50 max-w-md animate-in slide-in-from-bottom-4 duration-300',
      position === 'top' && 'max-w-none',
      className
    )}>
      <Alert 
        variant={isOnline ? "default" : "destructive"} 
        className={cn(
          "shadow-lg border-2",
          isOnline 
            ? "bg-green-50 border-green-200 text-green-800" 
            : "bg-amber-50 border-amber-200 text-amber-800",
          position === 'top' && 'rounded-none'
        )}
      >
        <div className="flex items-center gap-2">
          {isOnline ? (
            <Wifi className="h-4 w-4 text-green-600" />
          ) : (
            <>
              <WifiOff className="h-4 w-4 text-amber-600" />
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            </>
          )}
          <AlertDescription className="flex-1">
            {isOnline
              ? "ກັບມາອອນລາຍແລ້ວ - ຂໍ້ມູນຈະຖືກອັບເດດ"
              : "ບໍ່ມີການເຊື່ອມຕໍ່ອິນເຕີເນັດ - ກຳລັງໃຊ້ຂໍ້ມູນຈາກ cache"}
          </AlertDescription>
          {!isOnline && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => window.location.reload()}
              className="h-7 px-2 text-amber-700 hover:text-amber-900 hover:bg-amber-100"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              ລອງໃໝ່
            </Button>
          )}
        </div>
      </Alert>
    </div>
  );
}

/**
 * Compact offline indicator for header/navbar
 */
export function OfflineIndicatorCompact({ className }: { className?: string }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  if (isOnline) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-100 text-amber-700 text-xs font-medium animate-pulse",
        className
      )}
      role="status"
      aria-label="ບໍ່ມີການເຊື່ອມຕໍ່ອິນເຕີເນັດ"
    >
      <WifiOff className="h-3 w-3" />
      <span>Offline</span>
    </div>
  );
}

/**
 * Full-width banner for top of page
 */
export function OfflineBanner({ className }: { className?: string }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(!navigator.onLine);
  const [showBanner, setShowBanner] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowBanner(true);
      setTimeout(() => {
        setShowBanner(false);
        setWasOffline(false);
      }, 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      setShowBanner(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  if (!showBanner) {
    return null;
  }

  return (
    <div
      className={cn(
        "w-full px-4 py-2 text-center text-sm font-medium transition-all duration-300",
        isOnline 
          ? "bg-green-500 text-white" 
          : "bg-amber-500 text-white",
        className
      )}
      role="alert"
      aria-live="polite"
    >
      {isOnline ? (
        <div className="flex items-center justify-center gap-2">
          <Wifi className="h-4 w-4" />
          <span>ກັບມາອອນລາຍແລ້ວ - ຂໍ້ມູນຈະຖືກອັບເດດ</span>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2">
          <WifiOff className="h-4 w-4" />
          <span>ບໍ່ມີການເຊື່ອມຕໍ່ອິນເຕີເນັດ - ກຳລັງໃຊ້ຂໍ້ມູນຈາກ cache</span>
        </div>
      )}
    </div>
  );
}

export default OfflineIndicator;
